import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationsCtx = createContext(null);

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SOUND_PREF_KEY = "qq.sound_enabled";
const SEEN_KEY = "qq.notif_seen"; // ids we've already played a sound for

const readSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
};
const writeSeen = (set) => {
  const arr = Array.from(set);
  // keep only the latest 200 ids
  const trimmed = arr.slice(-200);
  localStorage.setItem(SEEN_KEY, JSON.stringify(trimmed));
};

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const v = localStorage.getItem(SOUND_PREF_KEY);
    return v === null ? true : v === "true";
  });
  const audioRef = useRef(null);
  const wsRef = useRef(null);
  const seenRef = useRef(readSeen());
  const retryRef = useRef(0);
  const closedRef = useRef(false);

  // Prepare audio element once
  useEffect(() => {
    const a = new Audio("/notification.wav");
    a.preload = "auto";
    a.volume = 0.6;
    audioRef.current = a;
  }, []);

  const playSound = useCallback(() => {
    if (!soundEnabled || !audioRef.current) return;
    try {
      audioRef.current.currentTime = 0;
      const p = audioRef.current.play();
      if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked */ });
    } catch { /* no-op */ }
  }, [soundEnabled]);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* no-op */ }
  }, [user]);

  const markRead = useCallback(async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setItems((xs) => xs.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* no-op */ }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setItems((xs) => xs.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch { /* no-op */ }
  }, []);

  const toggleSound = useCallback(async (val) => {
    setSoundEnabled(val);
    localStorage.setItem(SOUND_PREF_KEY, String(val));
    try { await api.patch("/me/preferences", { sound_enabled: val }); } catch { /* no-op */ }
  }, []);

  // WebSocket lifecycle
  useEffect(() => {
    if (!user) {
      if (wsRef.current) { closedRef.current = true; try { wsRef.current.close(); } catch {} wsRef.current = null; }
      setItems([]); setUnread(0);
      return;
    }
    closedRef.current = false;
    refresh();

    const connect = () => {
      try {
        const token = localStorage.getItem("qq_token") || "";
        const base = BACKEND.replace(/^http/, "ws");
        const url = `${base}/api/ws/notifications?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => { retryRef.current = 0; };
        ws.onmessage = (ev) => {
          let msg = null;
          try { msg = JSON.parse(ev.data); } catch { return; }
          if (msg?.event === "notification" && msg.data) {
            const n = msg.data;
            setItems((xs) => [n, ...xs.filter((x) => x.id !== n.id)].slice(0, 50));
            setUnread((u) => u + (n.is_read ? 0 : 1));
            if (!seenRef.current.has(n.id)) {
              seenRef.current.add(n.id);
              writeSeen(seenRef.current);
              playSound();
              toast(n.title, { description: n.message });
            }
          }
        };
        ws.onclose = () => {
          wsRef.current = null;
          if (closedRef.current) return;
          const delay = Math.min(15000, 1000 * Math.pow(2, retryRef.current++));
          setTimeout(connect, delay);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch { /* no-op */ }
    };
    connect();

    return () => {
      closedRef.current = true;
      if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    };
    // eslint-disable-next-line
  }, [user?.id]);

  const value = { items, unread, soundEnabled, refresh, markRead, markAllRead, toggleSound };
  return <NotificationsCtx.Provider value={value}>{children}</NotificationsCtx.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationsCtx);
  if (!ctx) throw new Error("useNotifications must be inside NotificationsProvider");
  return ctx;
}
